import { useEffect, useState } from 'react';
import { Progress, Table, TableColumn } from '@backstage/core-components';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import { AzureResourceDetail } from './AzureResourceDetail';
import type { AzureRow, AzureSectionState, ServiceCostResponse } from './azure';
import { azureSectionState, staleSources, toAzureRows } from './azure';
import { KUBERNETES_ID_ANNOTATION, formatCost } from './lib';

// Distinct from AzureSectionState, which only models post-response outcomes.
type AzureCardState = { kind: 'loading' } | AzureSectionState;

const columns: TableColumn<AzureRow>[] = [
  { title: 'Environment', field: 'environment' },
  {
    title: 'Total',
    field: 'totalCost',
    render: row => formatCost(row.totalCost),
  },
];

function FreshnessCaption({ response }: { response: ServiceCostResponse }) {
  const stale = staleSources(response);
  const neverLoaded = stale.filter(source => source.asOf === null);
  const staleWithData = stale.filter(source => source.asOf !== null);

  return (
    <>
      {staleWithData.map(source => (
        <Typography key={source.name} variant="body2" color="textSecondary">
          Some figures may be stale — {source.name} last updated{' '}
          {source.asOf}.
        </Typography>
      ))}
      {neverLoaded.map(source => (
        <Typography key={source.name} variant="body2" color="textSecondary">
          No data yet from {source.name}.
        </Typography>
      ))}
    </>
  );
}

export const AzureCostSection = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const kubernetesId =
    entity.metadata.annotations?.[KUBERNETES_ID_ANNOTATION] ?? '';

  const [state, setState] = useState<AzureCardState>(() =>
    configApi.getOptionalString('azureCost.baseUrl') && kubernetesId
      ? { kind: 'loading' }
      : { kind: 'hidden' },
  );

  useEffect(() => {
    let cancelled = false;
    const baseUrl = configApi.getOptionalString('azureCost.baseUrl');
    if (!baseUrl || !kubernetesId) {
      setState({ kind: 'hidden' });
      return undefined;
    }

    setState({ kind: 'loading' });
    const load = async () => {
      try {
        const response = await fetchApi.fetch(
          `${baseUrl}/service-costs?service=${encodeURIComponent(kubernetesId)}`,
        );
        let body: unknown;
        try {
          body = await response.json();
        } catch {
          body = undefined;
        }
        if (!cancelled) {
          setState(azureSectionState(response.status, body, kubernetesId));
        }
      } catch {
        if (!cancelled) {
          setState(azureSectionState(null, undefined, kubernetesId));
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [configApi, fetchApi, kubernetesId]);

  if (state.kind === 'hidden') {
    return null;
  }

  if (state.kind === 'loading') {
    return (
      <>
        <Divider style={{ margin: '16px 0' }} />
        <Typography variant="subtitle2">Azure resources</Typography>
        <Progress />
      </>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <>
        <Divider style={{ margin: '16px 0' }} />
        <Typography variant="subtitle2">Azure resources</Typography>
        <Typography variant="body2" color="textSecondary">
          Azure cost is currently unavailable.
        </Typography>
      </>
    );
  }

  if (state.kind === 'empty') {
    return (
      <>
        <Divider style={{ margin: '16px 0' }} />
        <Typography variant="subtitle2">Azure resources</Typography>
        <Typography variant="body2">
          No Azure resources are currently attributed to this service.
        </Typography>
      </>
    );
  }

  const rows = toAzureRows(state.response);

  return (
    <>
      <Divider style={{ margin: '16px 0' }} />
      <Typography variant="subtitle2">Azure resources</Typography>
      <Table
        options={{
          search: false,
          paging: false,
          toolbar: false,
          padding: 'dense',
        }}
        columns={columns}
        data={rows}
        detailPanel={({ rowData }: { rowData: AzureRow }) => (
          <AzureResourceDetail row={rowData} />
        )}
      />
      <FreshnessCaption response={state.response} />
    </>
  );
};
